/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import {
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSummaryOtb,
  selectAlignment,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { selectVisualFilters, selectIsSignedIn } from 'modules/dashboard/selectors';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import { TAGS, OTB_VALUE } from 'modules/dashboard/constants';
import {
  getDateComparisonFilter,
  getSubAlignmentFilter,
  getSubBreakdownFilter,
  getSubPaceFilter,
  getSubPaceVariableFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { ENVIRONMENT } from 'config';

/**
 * Custom hook to initialize summary filter options and alignment defaults
 * Preserves the restored date comparison and waits for visuals before replacing OTB.
 * @param {Boolean} isLoaded - Whether the visuals are ready for filter commands
 * @returns {Object} Date comparison, breakdown, pace horizon and pace variable option lists
 */
const useSummaryFilterOptions = (isLoaded) => {
  const dispatch = useDispatch();
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const visualFilters = useSelector(selectVisualFilters);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const isSignIn = useSelector(selectIsSignedIn);
  const customBooking = useSelector(selectSummaryOtb);
  const [dateComparisonList, setDateComparisonList] = useState([]);
  const [chartBreakdownList, setChartBreakdownList] = useState([]);
  const [paceList, setPaceList] = useState([]);
  const [paceVariableList, setPaceVariableList] = useState([]);
  // Triggered when visualFilters value changes
  useEffect(() => {
    // Set pace filter list and breakdown list
    const subBreakdownFilter = getSubBreakdownFilter(visualFilters, TAGS.BREAKDOWN_SUMMARY);
    if (subBreakdownFilter) {
      setChartBreakdownList(subBreakdownFilter);
    }
    const subPaceFilter = getSubPaceFilter(visualFilters);
    if (subPaceFilter) {
      setPaceList(subPaceFilter);
    }
    const subPaceVariableFilter = getSubPaceVariableFilter(visualFilters);
    if (subPaceVariableFilter) {
      setPaceVariableList(subPaceVariableFilter);
    }
  }, [visualFilters]);

  // Triggered when alignment, visualFilters, alignmentToggle changes
  useEffect(() => {
    if (!alignment) return;

    const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
    setDateComparisonList(dateComparisonFilter);
    // Preserve the restored comparison selection on browser refresh.
    const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
    if (!isSignIn && serializedState) return;

    dispatch(
      summaryActions.setDateComparison(
        dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
  }, [alignment, visualFilters, alignmentToggle]);

  // Triggered when alignmentToggle or visualFilters value changes
  useEffect(() => {
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, alignmentToggle);
    dispatch(
      summaryActions.setAlignment({
        id: subAlignmentFilter?.id,
        label: capitalizeFirst(subAlignmentFilter?.type),
      })
    );
  }, [alignmentToggle, visualFilters]);

  // Triggered when customBooking, dateComparison, visualFilters, isLoaded change
  useEffect(() => {
    // Assign default value to date comparison when OTB is selected for booking window
    if (!customBooking && dateComparison === OTB_VALUE && isLoaded) {
      const subAlignmentFilter = getSubAlignmentFilter(visualFilters, true);
      dispatch(
        summaryActions.setDateComparison(
          subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
        )
      );
    }
  }, [customBooking, dateComparison, visualFilters, isLoaded]);

  return { dateComparisonList, chartBreakdownList, paceList, paceVariableList };
};

export default useSummaryFilterOptions;
